import { Link, NavLink } from 'react-router-dom';
import { Mail, ExternalLink, Briefcase, Send } from 'lucide-react';
import { SP_URL, SP_EMAIL, SP_MAILTO } from '../lib/brand';

const NAV_LINKS = [
  { to: '/', label: 'Analyse', id: 'analyse' },
  { to: '/markets', label: 'Markets', id: 'markets' },
  { to: '/tools', label: 'Tools', id: 'tools' },
];

export const BrandMark = () => (
  <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
    <img src="/pmi-icon-192.png" alt="Trade PMI" className="w-7 h-7 rounded-md transition-transform group-hover:scale-105" />
    <span className="font-bold text-sm sm:text-base tracking-tight">Trade PMI</span>
    <span className="text-zinc-500 text-xs hidden md:inline">by Standards Protocol</span>
  </Link>
);

export const BrandNav = ({ children, width = 'max-w-6xl' }) => (
  <nav className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-zinc-800/60" data-testid="brand-nav">
    <div className={`${width} mx-auto px-4`}>
      <div className="flex items-center justify-between h-14 gap-3">
        <BrandMark />
        <div className="flex items-center gap-3 sm:gap-5">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) => `text-xs transition-colors ${isActive ? 'text-brand' : 'text-zinc-500 hover:text-white'}`}
            >
              {l.label}
            </NavLink>
          ))}
          {children}
        </div>
      </div>
    </div>
  </nav>
);

export const SiteFooter = ({ fixed = false }) => (
  <footer
    className={fixed
      ? 'fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-zinc-800/40 py-2.5 z-40'
      : 'border-t border-zinc-800/40 py-3 px-4 mt-8'}
    data-testid="site-footer"
  >
    <div className="flex items-center justify-center gap-x-3 gap-y-1 flex-wrap px-4 text-[11px] text-zinc-500">
      <p>Descriptive data only. Not financial advice.</p>
      <span className="text-zinc-700 hidden sm:inline">|</span>
      <p>
        Trade PMI by <a href={SP_URL} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-brand transition-colors" data-testid="footer-sp-link">Standards Protocol</a>
      </p>
      <span className="text-zinc-700 hidden sm:inline">|</span>
      <Link to="/contact" className="hover:text-brand transition-colors" data-testid="footer-contact-link">Contact</Link>
      <Link to="/usage" className="hover:text-zinc-400 transition-colors" data-testid="footer-usage-link">Usage</Link>
      <Link to="/privacy" className="hover:text-zinc-400 transition-colors" data-testid="footer-privacy-link">Privacy</Link>
      <Link to="/terms" className="hover:text-zinc-400 transition-colors" data-testid="footer-terms-link">Terms</Link>
    </div>
  </footer>
);

export const ContactCTA = ({ className = '', source = 'direct', ticker = '' }) => (
  <div className={`bg-zinc-950 border border-brand/25 rounded-lg p-5 ${className}`} data-testid="standards-protocol-cta">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded bg-brand/10 flex items-center justify-center">
        <Briefcase className="w-4 h-4 text-brand" />
      </div>
      <span className="font-bold text-sm">Need something tailored?</span>
    </div>
    <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
      Standards Protocol builds custom market-intelligence solutions, white-label platforms and business partnerships. Talk to the team about what you need.
    </p>
    <div className="flex flex-wrap gap-3">
      <Link to={`/contact?from=${source}${ticker ? `&t=${encodeURIComponent(ticker)}` : ''}`} className="bg-brand hover:bg-brand-light text-black text-xs font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors" data-testid="cta-enquiry-link">
        <Send className="w-3.5 h-3.5" /> Send an enquiry
      </Link>
      <a href={SP_MAILTO} className="border border-zinc-700 hover:border-brand/60 text-zinc-300 hover:text-white text-xs px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors" data-testid="cta-email-link">
        <Mail className="w-3.5 h-3.5" /> {SP_EMAIL}
      </a>
      <a href={SP_URL} target="_blank" rel="noopener noreferrer" className="border border-zinc-700 hover:border-brand/60 text-zinc-300 hover:text-white text-xs px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors" data-testid="cta-website-link">
        standardsprotocol.com <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  </div>
);
